import { useState, useEffect, useCallback } from 'react';
import ptaeService from '../service/ptaeService.mjs';



export const usePTAEManagement = () => {
  const [ptaes, setPtaes] = useState([]);
  const [uniqueColumnAs, setUniqueColumnAs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadPTAEs = useCallback(async (columnA = '', search = '', region = '') => {
    setLoading(true);
    setError(null);
    try {
      // Management view shows all PTAEs including suspended ones.
      const data = await ptaeService.getPTAEs(columnA, search, true, region);
      setPtaes(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUniqueColumnAs = useCallback(async () => {
    try {
      const data = await ptaeService.getUniqueColumnAs();
      setUniqueColumnAs(data);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    loadPTAEs();
    loadUniqueColumnAs();
  }, [loadPTAEs, loadUniqueColumnAs]);

  /**
   * Every mutation below is a REQUEST to the finance director, not a change.
   *
   * So the optimistic state records the request - `approvalStatus: 'pending'` for
   * an addition, `pendingAction` for an edit, suspension or reactivation - and
   * deliberately does NOT flip the field being requested. Showing a code as
   * suspended when it is still selectable would be worse than showing nothing.
   */
  const addPTAE = async (id, description, reason, region = '') => {
    setSaving(true);
    setError(null);
    try {
      const newPtae = await ptaeService.addPTAE(id, description, reason, region);
      // Asking again for a code the finance director rejected re-opens that
      // row on the server, so replace it here rather than listing it twice.
      setPtaes(prev => (prev.some(p => p.id === newPtae.id)
        ? prev.map(p => (p.id === newPtae.id ? newPtae : p))
        : [...prev, newPtae]));
      return newPtae;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // `id` is the code the row currently has. A rename is only applied when the
  // finance director approves, so the row keeps its current code here and is
  // simply marked as having an edit outstanding.
  const updatePTAE = async (id, changes) => {
    setSaving(true);
    setError(null);
    try {
      const result = await ptaeService.updatePTAE(id, changes);
      setPtaes(prev => prev.map(ptae => (
        ptae.id === id ? { ...ptae, ...result, id: ptae.id, pendingAction: 'edit' } : ptae
      )));
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const suspendPTAE = async (id, reason, region = '') => {
    setSaving(true);
    setError(null);
    try {
      const result = await ptaeService.suspendPTAE(id, reason, region);
      setPtaes(prev => prev.map(ptae => (
        ptae.id === id ? { ...ptae, pendingAction: 'suspend' } : ptae
      )));
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const reactivatePTAE = async (id, reason, region = '') => {
    setSaving(true);
    setError(null);
    try {
      const result = await ptaeService.reactivatePTAE(id, reason, region);
      setPtaes(prev => prev.map(ptae => (
        ptae.id === id ? { ...ptae, pendingAction: 'reactivate' } : ptae
      )));
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  return {
    ptaes,
    uniqueColumnAs,
    loading,
    error,
    saving,
    loadPTAEs,
    addPTAE,
    updatePTAE,
    suspendPTAE,
    reactivatePTAE,
    setError
  };
};